/**
 * lobbyStore — Zustand store reflecting server-authoritative LobbyState.
 *
 * The server is the source of truth. This store holds the latest
 * `LobbyState` snapshot received from a `lobbyState` ServerMsg and the
 * identity carried from the `hello` handshake (sessionId, playerId).
 *
 * All "actions" are dispatchers — they call `wsClient.send(...)` rather
 * than mutating state locally. Local mutations would race with server
 * broadcasts and produce roster flicker; instead the UI waits for the
 * authoritative `lobbyState` reply and re-renders from that.
 *
 * Stale-frame protection: incoming `lobbyState` messages with `seq` lower
 * than the last applied are dropped. This guards against out-of-order
 * delivery on reconnect.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import {
  asAuthToken,
  asPlayerId,
  asSessionId,
  type AuthToken,
  type LobbyPlayer,
  type LobbyState,
  type PlayerColorKey,
  type PlayerId,
  type ServerMsg,
  type SessionId,
  type SessionSettings,
} from '@qd/protocol';

import { connect as wsConnect, type WsClient, type WsState } from '../net/wsClient';

// -----------------------------------------------------------------------------
// localStorage helpers — last-known joiner identity (name + colour).
//
// Why: after a long idle the WebSocket can drop. The wsClient reconnects
// transparently, but if our React state was lost (route remount, state
// store reset on hot reload, etc.) the joiner's name/color is gone —
// which leaves them as a ghost (server slot dropped after grace, or
// reattached but with no UI re-fire of `joinSession`).
//
// Persisting the identity means the store can replay `joinSession`
// automatically on every transport reconnect, both to keep the server
// roster slot populated under normal token-reattach AND to recover
// gracefully when the server has lost the player entirely (token map
// wiped, grace expired). The token in the URL handles fast re-attach;
// this replay handles slow / lost-token recovery.
// -----------------------------------------------------------------------------

const IDENTITY_PREFIX = 'qd_lobby_identity_';

interface PersistedIdentity {
  name: string;
  color: PlayerColorKey;
}

function loadIdentity(sessionId: SessionId): PersistedIdentity | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(IDENTITY_PREFIX + sessionId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedIdentity>;
    if (
      typeof parsed.name === 'string' &&
      typeof parsed.color === 'string' &&
      parsed.name.length > 0 &&
      parsed.color.length > 0
    ) {
      return { name: parsed.name, color: parsed.color as PlayerColorKey };
    }
    return null;
  } catch {
    return null;
  }
}

function saveIdentity(sessionId: SessionId, identity: PersistedIdentity): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      IDENTITY_PREFIX + sessionId,
      JSON.stringify(identity),
    );
  } catch {
    // localStorage may be disabled (Safari private mode); silently degrade.
  }
}


interface LobbyStoreState {
  // ─── Connection identity ───
  /** Active WebSocket client. Null when disconnected. */
  client: WsClient | null;
  /** Last observed transport state, surfaced for the reconnect overlay. */
  wsState: WsState;
  /** Session id we're currently joined to (sourced from `Hello` or route). */
  sessionId: SessionId | null;
  /** Server-issued player id for *this* browser tab (sourced from `Hello`). */
  selfId: PlayerId | null;
  /** Server-issued auth token; persisted to localStorage by wsClient. */
  selfToken: AuthToken | null;

  // ─── Server-pushed lobby snapshot ───
  /** Latest authoritative LobbyState; null while connecting. */
  lobbyState: LobbyState | null;
  /** Most recently applied `seq`; used to drop stale frames. */
  lastSeq: number;

  // ─── Lifecycle ───
  /**
   * Open a WebSocket to the given session. If `join` is provided, the
   * wsClient sends a `joinSession` handshake immediately on `Hello` so
   * the player is seated in the roster. Calling `connect` while already
   * connected to the same session is a no-op; switching session ids
   * cleanly closes the old socket first.
   */
  connect(
    sessionId: SessionId,
    join?: { name?: string; color?: PlayerColorKey },
  ): void;
  /** Close the current socket and clear connection-scoped state. */
  disconnect(): void;

  // ─── Action dispatchers (all route to the server) ───
  joinSession(name: string, color: PlayerColorKey): void;
  setName(name: string): void;
  setColor(color: PlayerColorKey): void;
  toggleReady(): void;
  updateSettings(patch: Partial<SessionSettings>): void;
  kickPlayer(playerId: PlayerId): void;
  beginMatch(): void;
}

export const useLobbyStore = create<LobbyStoreState>((set, get) => ({
  client: null,
  wsState: 'closed',
  sessionId: null,
  selfId: null,
  selfToken: null,
  lobbyState: null,
  lastSeq: -1,

  connect(sessionId, join) {
    const current = get();

    // Idempotent: if we're already wired to this session and the socket
    // hasn't been closed, leave it alone. Switching sessions tears down
    // the old client first.
    if (current.client && current.sessionId === sessionId) {
      return;
    }
    if (current.client) {
      current.client.close();
    }

    // Resolve the auto-join payload. Explicit `join` arg wins (admin
    // route passes its default identity), otherwise fall back to a
    // previously-persisted joiner identity so a reload / route remount
    // doesn't strand the user as a ghost. The wsClient replays this on
    // every `Hello`, so reconnects after long idle automatically
    // re-attach the player slot.
    const persisted = !join ? loadIdentity(sessionId) : null;
    const effectiveJoin =
      join ?? (persisted ? { name: persisted.name, color: persisted.color } : undefined);

    const client = wsConnect(
      effectiveJoin ? { sessionId, join: effectiveJoin } : { sessionId },
    );

    const offMessage = client.onMessage((msg: ServerMsg) => {
      handleServerMessage(set, get, msg);
    });
    // Track previous transport state so we can detect a reconnect edge
    // (anything → 'open' after we've already been open at least once)
    // and re-fire `joinSession` from the persisted identity. This is
    // belt-and-braces alongside wsClient's join-on-Hello replay: it
    // covers the case where the user joined this session AFTER the
    // initial connect (so the wsClient was constructed without a
    // `join` payload) and only later picked a name in JoinFlow.
    let hasBeenOpen = false;
    const offState = client.onStateChange((wsState: WsState) => {
      const prev = get().wsState;
      set({ wsState });
      if (wsState === 'open') {
        if (hasBeenOpen && prev !== 'open') {
          // Reconnect edge — replay identity if we have one.
          const id = loadIdentity(sessionId);
          const tok = get().selfToken;
          if (id) {
            client.send({
              type: 'joinSession',
              ...(tok ? { token: tok } : {}),
              name: id.name,
              color: id.color,
            });
          }
        }
        hasBeenOpen = true;
      }
    });

    // Wrap close to also tear down our subscriptions. (wsClient.close()
    // already clears its own handler sets, but this keeps the contract
    // explicit if a future caller closes via the client directly.)
    const wrappedClient: WsClient = {
      send: client.send.bind(client),
      onMessage: client.onMessage.bind(client),
      onStateChange: client.onStateChange.bind(client),
      getState: client.getState.bind(client),
      close() {
        offMessage();
        offState();
        client.close();
      },
    };

    set({
      client: wrappedClient,
      wsState: client.getState(),
      sessionId,
      lobbyState: null,
      lastSeq: -1,
    });
  },

  disconnect() {
    const { client } = get();
    if (client) client.close();
    // Note: we deliberately do NOT clear the persisted identity here.
    // The user may navigate /lobby → /play → back to /lobby (e.g. after
    // a match ends) and we want the rejoin to be seamless. Identity is
    // only cleared on an explicit `leaveSession` action (future work).
    set({
      client: null,
      wsState: 'closed',
      sessionId: null,
      selfId: null,
      selfToken: null,
      lobbyState: null,
      lastSeq: -1,
    });
  },

  joinSession(name, color) {
    const { client, selfToken, sessionId } = get();
    if (!client) return;
    // Persist the chosen identity so a transport reconnect (after long
    // idle, mobile sleep, Worker eviction, etc.) can replay it without
    // walking the user back through the JoinFlow stepper.
    if (sessionId) {
      saveIdentity(sessionId, { name, color });
    }
    client.send({
      type: 'joinSession',
      ...(selfToken ? { token: selfToken } : {}),
      name,
      color,
    });
  },

  setName(name) {
    const { client, sessionId, lobbyState, selfId } = get();
    if (!client) return;
    // Keep the persisted identity in sync. The current colour comes from
    // the server-pushed roster (if available) so we don't clobber it
    // with stale local state.
    if (sessionId) {
      const self = lobbyState?.players.find((p) => p.id === selfId);
      if (self?.color) {
        saveIdentity(sessionId, { name, color: self.color });
      }
    }
    client.send({ type: 'setName', name });
  },

  setColor(color) {
    const { client, sessionId, lobbyState, selfId } = get();
    if (!client) return;
    if (sessionId) {
      const self = lobbyState?.players.find((p) => p.id === selfId);
      if (self?.name) {
        saveIdentity(sessionId, { name: self.name, color });
      }
    }
    client.send({ type: 'setColor', color });
  },

  toggleReady() {
    get().client?.send({ type: 'toggleReady' });
  },

  updateSettings(settings) {
    get().client?.send({ type: 'updateSettings', settings });
  },

  kickPlayer(playerId) {
    get().client?.send({ type: 'kickPlayer', playerId });
  },

  beginMatch() {
    get().client?.send({ type: 'beginMatch' });
  },
}));

// -----------------------------------------------------------------------------
// Reducer for incoming server messages
// -----------------------------------------------------------------------------

function handleServerMessage(
  set: (
    partial:
      | Partial<LobbyStoreState>
      | ((prev: LobbyStoreState) => Partial<LobbyStoreState>),
  ) => void,
  get: () => LobbyStoreState,
  msg: ServerMsg,
): void {
  switch (msg.type) {
    case 'hello': {
      set({
        sessionId: asSessionId(msg.sessionId),
        selfId: asPlayerId(msg.playerId),
        selfToken: asAuthToken(msg.token),
      });
      return;
    }
    case 'lobbyState': {
      const incoming = msg.state;
      const last = get().lastSeq;
      // Drop frames whose seq is older than what we've already applied
      // (out-of-order delivery on reconnect).
      if (incoming.seq < last) return;
      set({ lobbyState: incoming, lastSeq: incoming.seq });
      return;
    }
    case 'error': {
      // For now, just log. Phase 5+ will surface validation errors in the
      // UI via a toast layer.
      // eslint-disable-next-line no-console
      console.warn('[lobbyStore] server error', msg.code, msg.message);
      return;
    }
    case 'pong':
      // Heartbeat reply; ignore for now (no client-side ping yet).
      return;
  }
}

// -----------------------------------------------------------------------------
// Selectors — typed convenience hooks for components
// -----------------------------------------------------------------------------

const EMPTY_PLAYERS: readonly LobbyPlayer[] = [];

export function useSelfPlayer(): LobbyPlayer | null {
  return useLobbyStore((s) => {
    const selfId = s.selfId;
    if (!selfId || !s.lobbyState) return null;
    return s.lobbyState.players.find((p) => p.id === selfId) ?? null;
  });
}

export function usePlayers(): readonly LobbyPlayer[] {
  return useLobbyStore((s) => s.lobbyState?.players ?? EMPTY_PLAYERS);
}

export function useIsAdmin(): boolean {
  return useLobbyStore((s) => {
    const selfId = s.selfId;
    if (!selfId || !s.lobbyState) return false;
    return s.lobbyState.hostId === selfId;
  });
}

export function useTakenColors(): ReadonlySet<PlayerColorKey> {
  const players = usePlayers();
  return useMemo(() => {
    const set = new Set<PlayerColorKey>();
    for (const p of players) {
      if (p.color) set.add(p.color);
    }
    return set;
  }, [players]);
}

export function useLobbyPhase(): LobbyState['phase'] | null {
  return useLobbyStore((s) => s.lobbyState?.phase ?? null);
}

export function useWsState(): WsState {
  return useLobbyStore((s) => s.wsState);
}

export function useSessionSettings(): SessionSettings | null {
  return useLobbyStore((s) => s.lobbyState?.settings ?? null);
}

/** Maximum supported players; sourced from MAX_PLAYERS contract on the server. */
export const LOBBY_MAX_PLAYERS = 5;
